import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import { api, money, moneyFull, pct } from '../../lib/api';
import { SectionHeader } from '../../components/UI';
import { DataSourceBadge } from '../../components/sales/DataSourceBadge';
import { ClockLabel } from '../../components/sales/ClockLabel';
import { PlanInconsistencyBadge } from '../../components/sales/PlanInconsistencyBadge';
import { Target, TrendingUp, TrendingDown, Lightbulb, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
// M2 · Sales Plan — "Are we going to hit BHAG, and if not, what's the biggest reason?"
// Uses Revenue Target - ADJUSTED as the plan baseline. Projected year-end is a
// deterministic run-rate extrapolation. Biggest-gap driver decomposes the
// current gap into funnel factors (calls, show-up, close rate, avg price) and
// reports the single factor whose return-to-target would recover the most
// revenue.

const CHART_GRID = { strokeDasharray: '2 4', stroke: '#141B4D', strokeOpacity: 0.1 };
const CHART_AXIS = { stroke: '#141B4D', strokeOpacity: 0.3 };
const CHART_TOOLTIP = { background: '#FFFFFF', border: '1px solid #141B4D', color: '#141B4D', borderRadius: 0, fontSize: 12 };

function GapContributorRow({ c, factor }) {
  const label = c.factor;
  const isRate = ['Qualification Rate (of Calls Had)', 'Close Rate', 'No-show Rate', 'Show-up Rate'].includes(label);
  const fmt = (v) => isRate ? pct(v, 1) : (label === 'Avg Price Point' ? money(v) : v.toLocaleString());
  return (
    <li
      className={`border-t border-indie-ink/10 first:border-t-0 py-2 flex items-center justify-between ${c === factor ? 'bg-indie-yellow/20 px-2 -mx-2' : ''}`}
      data-testid={`plan-gap-contributor-${label.toLowerCase().replace(/[\s()]+/g, '-').replace(/-$/,'')}`}
    >
      <div className="flex flex-col">
        <span className="text-sm text-indie-ink font-medium">{label}</span>
        <span className="text-xs text-indie-ink/60">
          Actual {fmt(c.actual)} · Target {fmt(c.target)}
        </span>
      </div>
      <div className="text-sm font-semibold text-indie-ink text-right">
        + {money(c.revenue_delta_if_fixed)}
        <div className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/50 font-normal">
          if fixed
        </div>
      </div>
    </li>
  );
}

export default function SalesPlan() {
  const [d, setD] = useState(null);
  const [ts, setTs] = useState(null);
  const [rem, setRem] = useState(null);
  const [bhag, setBhag] = useState(null);
  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  // Hero framing uses the ADJUSTED plan (the goal we're steering to).
  // Attainment ratios use ORIGINAL. Both bases are returned in every payload
  // — the `basis` query param only steers which is echoed as the primary
  // `annual_target` field for legacy consumers.
  const basis = 'adjusted';

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/sales/plan?basis=${basis}`),
      api.get('/sales/timeseries?include_current=true').catch(() => ({ data: null })),
      api.get(`/sales/plan/remaining-year?basis=${basis}`).catch(() => ({ data: null })),
      api.get('/bhag').catch(() => ({ data: null })),
      api.get('/sales/plan/year-end-playbook').catch(() => ({ data: null })),
    ])
      .then(([p, t, r, b, pb]) => { setD(p.data); setTs(t.data); setRem(r.data); setBhag(b.data); setPlaybook(pb.data); })
      .catch(() => toast.error('Could not load Sales Plan.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="label-editorial" data-testid="plan-loading">Loading Sales Plan…</div>;
  if (!d) return <div className="label-editorial">No plan data.</div>;

  // Monthly Plan-vs-Actual chart data — three lines:
  //   • Actual        : BHAG revenue_actual · stops at last completed month
  //   • Adjusted plan : past months replaced with actuals; future months use
  //                     BHAG row 3 Adjusted target so year-end reflects
  //                     reality-to-date plus planned paid-ad ramp.
  //   • Original plan : BHAG row 2 Revenue Target — ORIGINAL, all 12 months.
  //                     This is the historical attainment baseline.
  const monthShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const bhagMonths = bhag?.months || [];
  const bhagByMonth = Object.fromEntries(bhagMonths.map((m) => [m.month, m]));
  const cutoffMonth = ts?.cutoff_month || 6;

  const chartData = monthShort.map((label, i) => {
    const mnum = i + 1;
    const bm = bhagByMonth[mnum] || {};
    const isPast = mnum <= cutoffMonth;
    const actual = bm.revenue_actual ?? null;
    const adjustedTarget = bm.revenue_target ?? null;   // BHAG row 3 "Adjusted"
    const originalTarget = bm.revenue_target_original ?? null;
    return {
      label,
      actual: isPast ? actual : null,                             // only past
      adjusted: isPast ? actual : adjustedTarget,                 // hybrid line
      original: originalTarget,                                   // all 12 months
    };
  });

  const projectedOnTrack = d.projected_delta_vs_annual != null && d.projected_delta_vs_annual >= 0;
  const gap = d.biggest_gap_driver;

  const adjustedAnnual = d.adjusted_annual_target ?? d.annual_target;
  const originalAnnual = d.original_annual_target;
  const attainmentOriginal = d.ytd_attainment_vs_original;

  return (
    <div className="space-y-6" data-testid="sales-plan-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <SectionHeader
          eyebrow="Sales · Plan"
          title="Are we going to hit the adjusted revenue target?"
          description="Forward plan focuses on the ADJUSTED Revenue Target (BHAG row 3) — Joey's active goal, already reflecting the planned paid-ad ramp. Historical attainment stays anchored to the ORIGINAL target (row 2) so closed-month ratios aren't silently inflated."
        />
        <DataSourceBadge
          source={d.sources?.hero_target || 'BHAG · Adjusted Revenue Target'}
          asOf={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          confidence="green"
          testId="plan-source-badge"
        />
      </div>

      {/* Hero — Adjusted annual target (primary) + YTD attainment vs ORIGINAL + BHAG-trajectory projection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="plan-hero">
        <div className="border-2 border-indie-navy bg-indie-navy text-indie-cream p-5" data-testid="plan-adjusted-target">
          <div className="flex items-center gap-2 flex-wrap">
            <ClockLabel type="cash" />
            <span className="text-[10px] uppercase tracking-[0.16em] text-indie-cream/60">Adjusted revenue target · 2026</span>
          </div>
          <div className="mt-2 text-3xl font-semibold num-tabular">{moneyFull(adjustedAnnual)}</div>
          <div className="mt-1 text-xs text-indie-cream/60">
            BHAG row 3 · includes planned paid-ad ramp
            {originalAnnual && originalAnnual !== adjustedAnnual && (
              <> · original was {moneyFull(originalAnnual)}</>
            )}
          </div>
        </div>
        <div className="border-2 border-indie-ink/20 p-5" data-testid="plan-ytd">
          <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
            YTD actual · attainment vs ORIGINAL
          </div>
          <div className="mt-2 text-3xl font-semibold text-indie-ink num-tabular">{moneyFull(d.ytd_actual)}</div>
          <div className="mt-1 text-xs text-indie-ink/70">
            Original target through same window: {money(d.ytd_original_target)}
            {attainmentOriginal != null && (
              <> · <span className="font-semibold">{pct(attainmentOriginal, 0)}</span></>
            )}
          </div>
          <div className="mt-1 text-[10px] text-indie-ink/50">
            Historical attainment uses BHAG row 2 (original), never adjusted.
          </div>
        </div>
        <div
          className={`border-2 p-5 ${projectedOnTrack
            ? 'border-emerald-600 bg-emerald-50'
            : 'border-rose-600 bg-rose-50'}`}
          data-testid="plan-projected"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] font-semibold text-indie-ink/60">
            Projected year-end · BHAG trajectory
            {projectedOnTrack
              ? <TrendingUp size={12} className="text-emerald-700" />
              : <TrendingDown size={12} className="text-rose-700" />}
          </div>
          <div className="mt-2 text-3xl font-semibold text-indie-ink num-tabular">{moneyFull(d.projected_year_end)}</div>
          {d.projected_delta_vs_annual != null && (
            <div className={`mt-1 text-xs font-semibold ${projectedOnTrack ? 'text-emerald-700' : 'text-rose-700'}`}>
              {projectedOnTrack ? '+' : ''}{money(d.projected_delta_vs_annual)} vs adjusted target
            </div>
          )}
          <div className="mt-1 text-[10px] text-indie-ink/60">
            Future months land on BHAG&apos;s adjusted plan (not a flat run-rate).
          </div>
        </div>
      </div>

      {/* Monthly Plan vs Actual · year view */}
      <div className="border border-indie-ink/15 bg-white p-5" data-testid="plan-monthly-chart">
        <div className="flex items-center gap-2 flex-wrap">
          <Target size={14} className="text-indie-ink/70" strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
            Plan vs actual · year view · monthly revenue
          </div>
          <ClockLabel type="cash" />
        </div>
        <div className="mt-1 text-[11px] text-indie-ink/60">
          Adjusted plan (blue, primary) is BHAG row 3 — the current active goal with planned paid-ad increases baked in.
          Original plan (dashed navy) is BHAG row 2 — the baseline set at the start of the year, used for historical attainment.
          Actual (teal) stops at the last completed month.
        </div>
        <div className="mt-3 h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ left: 20, right: 20, top: 20, bottom: 10 }}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="label" {...CHART_AXIS} tick={{ fontSize: 11 }} />
              <YAxis {...CHART_AXIS} tick={{ fontSize: 11 }}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={CHART_TOOLTIP}
                formatter={(v) => v == null ? '—' : moneyFull(v)}
                labelFormatter={(l) => `${l} 2026`} />
              <Legend iconType="rect" />
              <Line
                type="monotone" dataKey="adjusted" name="Adjusted plan (primary · past actuals + future adjusted)"
                stroke="#1D57A5" strokeWidth={3} dot={{ r: 3 }} connectNulls />
              <Line
                type="monotone" dataKey="original" name="Original plan (attainment baseline)"
                stroke="#141B4D" strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls />
              <Line
                type="monotone" dataKey="actual" name="Actual (closed months)"
                stroke="#0F766E" strokeWidth={3} dot={{ r: 5 }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Biggest-gap driver */}
      {gap && (
        <div className="border border-indie-ink/15 bg-white p-5" data-testid="plan-biggest-gap">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
              Biggest gap · {d.biggest_gap_month}
            </div>
            <PlanInconsistencyBadge reason="Deterministic funnel decomposition on BHAG scorecard rows. Assumes returning a single factor to target while holding others at actual." />
          </div>
          <div className="mt-3 text-lg text-indie-ink leading-snug">
            <span className="font-semibold">{gap.factor}</span> is the biggest single lever.
            Returning it from {['Qualification Rate (of Calls Had)','Close Rate','No-show Rate','Show-up Rate'].includes(gap.factor) ? pct(gap.actual, 1) : gap.actual.toLocaleString()}
            {' → '}
            {['Qualification Rate (of Calls Had)','Close Rate','No-show Rate','Show-up Rate'].includes(gap.factor) ? pct(gap.target, 1) : gap.target.toLocaleString()}
            {' '}would recover <span className="font-semibold">{money(gap.revenue_delta_if_fixed)}</span> in
            {' '}{d.biggest_gap_month} revenue.
          </div>
          <ul className="mt-4 border-t border-indie-ink/10">
            {gap.all_contributors.map((c) => <GapContributorRow key={c.factor} c={c} factor={gap} />)}
          </ul>
          <div className="mt-3 text-[11px] text-indie-ink/50">
            Funnel: revenue = Calls Had × Qualification Rate (of Calls Had) × Close Rate × Avg Price.
            Qualification Rate = Qual Calls Had ÷ Calls Held (BHAG &quot;Qual. %&quot; row). Baseline revenue
            reconstructed from funnel: {money(gap.baseline_revenue)}. Contributors assume the named factor
            returns to target with all others held at actual — non-additive across rows.
          </div>
        </div>
      )}

      {!gap && (
        <div className="border-2 border-dashed border-indie-ink/15 p-5 text-sm text-indie-ink/60" data-testid="plan-no-gap">
          Biggest-gap driver couldn&apos;t be computed for the latest month — the Sales Snapshot doesn&apos;t have all four funnel factors populated yet. It refreshes once Bianca completes the month-close backfill.
        </div>
      )}

      {/* Remaining-year outlook — the "where are we going?" tile.
          Uses BHAG adjusted-trajectory (row 3) for future months, so the
          projection reflects Joey's planned paid-ad ramp, not a flat
          run-rate. Required run rate and lift % remain as reference. */}
      {rem?.ok && (
        <div className="border border-indie-ink/15 bg-white p-5" data-testid="plan-remaining-year">
          <div className="flex items-center gap-2 flex-wrap">
            <TrendingUp size={14} className="text-indie-ink/70" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
              Remaining year outlook · {rem.remaining_months} month{rem.remaining_months === 1 ? '' : 's'} left · BHAG adjusted trajectory
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-indie-cream border border-indie-ink/10">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">Adjusted annual target</div>
              <div className="mt-1 text-lg font-semibold text-indie-ink num-tabular" data-testid="rem-annual-target">
                {moneyFull(rem.annual_target)}
              </div>
              {rem.annual_target_original && rem.annual_target_original !== rem.annual_target && (
                <div className="text-[10px] text-indie-ink/50 num-tabular">
                  Original: {moneyFull(rem.annual_target_original)}
                </div>
              )}
            </div>
            <div className="p-3 bg-indie-cream border border-indie-ink/10">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">YTD actual · {rem.closed_months} closed</div>
              <div className="mt-1 text-lg font-semibold text-indie-ink num-tabular" data-testid="rem-ytd-actual">
                {moneyFull(rem.ytd_actual)}
              </div>
              {rem.ytd_attainment_vs_original != null && (
                <div className="text-[10px] text-indie-ink/50 num-tabular">
                  vs original: {pct(rem.ytd_attainment_vs_original, 0)}
                </div>
              )}
            </div>
            <div className="p-3 bg-indie-cream border border-indie-ink/10">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">Remaining plan (adjusted)</div>
              <div className="mt-1 text-lg font-semibold text-indie-ink num-tabular" data-testid="rem-required-per-month">
                {moneyFull(rem.remaining_target_adjusted)}
              </div>
              <div className="text-[10px] text-indie-ink/50 num-tabular">
                {rem.remaining_months} month{rem.remaining_months === 1 ? '' : 's'} · avg pace needed {moneyFull(rem.required_monthly_run_rate)}
              </div>
            </div>
            <div className={`p-3 border-l-4 ${rem.projected_delta_vs_annual >= 0 ? 'border-emerald-600 bg-emerald-50' : 'border-rose-500 bg-rose-50'}`}>
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">Projected vs target</div>
              <div className="mt-1 text-lg font-semibold text-indie-ink num-tabular flex items-center gap-1" data-testid="rem-projected-delta">
                {rem.projected_delta_vs_annual >= 0 ? (
                  <TrendingUp size={14} className="text-emerald-700" strokeWidth={1.5} />
                ) : (
                  <TrendingDown size={14} className="text-rose-600" strokeWidth={1.5} />
                )}
                {rem.projected_delta_vs_annual >= 0 ? '+' : ''}{moneyFull(rem.projected_delta_vs_annual)}
              </div>
              <div className="text-[10px] text-indie-ink/60 num-tabular">
                Year-end (BHAG trajectory): {moneyFull(rem.projected_year_end)}
              </div>
              {rem.projected_year_end_flat_run_rate != null && (
                <div className="text-[10px] text-indie-ink/50 num-tabular">
                  Flat run-rate would land {moneyFull(rem.projected_year_end_flat_run_rate)}
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 text-[11px] text-indie-ink/60 leading-relaxed">
            The remaining {rem.remaining_months} month{rem.remaining_months === 1 ? '' : 's'} of BHAG&apos;s adjusted plan sum to <span className="font-semibold">{moneyFull(rem.remaining_target_adjusted)}</span> — already reflecting Joey&apos;s planned paid-ad increases.
            Combined with the {moneyFull(rem.ytd_actual)} closed to date, that lands at <span className="font-semibold">{moneyFull(rem.projected_year_end)}</span>.
            {rem.projected_delta_vs_annual != null && rem.projected_delta_vs_annual < 0 && (
              <> Adjusted plan undershoots the annual target by <span className="font-semibold">{moneyFull(-rem.projected_delta_vs_annual)}</span> if closed months aren&apos;t backfilled.</>
            )}
          </div>
          <div className="mt-2 text-[10px] text-indie-ink/50 pt-1 border-t border-indie-ink/10">
            Source: {rem.source}
          </div>
        </div>
      )}

      {/* Closing the year — dedicated forward-look section.
          Answers: "In consideration of YTD data, what do we want to see in
          future months to close the year as well as possible — and what are
          the potential barriers and strategies to overcome them?"
          One line chart, one supporting-metrics grid, one barriers panel. */}
      {playbook?.ok && playbook.remaining_months_count > 0 && (
        <div className="border-2 border-indie-navy bg-white p-5 space-y-5" data-testid="plan-year-end-playbook">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
                Closing the year · remaining {playbook.remaining_months_count} month{playbook.remaining_months_count === 1 ? '' : 's'} in focus
              </div>
              <div className="mt-1 text-lg font-semibold text-indie-ink leading-snug">
                What needs to be true from {playbook.remaining_months[0]?.label} through {playbook.remaining_months[playbook.remaining_months.length - 1]?.label}?
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">Gap to original</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular" data-testid="playbook-gap-original">
                {moneyFull(playbook.required_to_hit_original?.total_gap)}
              </div>
              <div className="text-[10px] text-indie-ink/50 num-tabular">
                Adjusted gap: {moneyFull(playbook.required_to_hit_adjusted?.total_gap)}
              </div>
            </div>
          </div>

          {/* Remaining-months line chart · revenue */}
          <div className="border border-indie-ink/10 bg-white p-4" data-testid="playbook-remaining-chart">
            <div className="flex items-center gap-2 flex-wrap">
              <Target size={13} className="text-indie-ink/70" strokeWidth={1.5} />
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">
                Monthly revenue · remaining months only
              </div>
              <ClockLabel type="cash" />
            </div>
            <div className="mt-1 text-[11px] text-indie-ink/60">
              Adjusted plan (blue, solid) is BHAG&apos;s active target for each remaining month. Required-to-hit-original
              (navy dashed) is the flat run rate needed to close on the original {moneyFull(playbook.original_annual_target)} baseline.
              Trailing {playbook.trailing_window}-month pace (teal dashed) is what we&apos;re actually producing today.
            </div>
            <div className="mt-3 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={(playbook.remaining_months || []).map((rm) => ({
                    label: rm.label?.slice(0, 3),
                    adjustedPlan: rm.revenue_target_adjusted,
                    originalPlan: rm.revenue_target_original,
                    requiredOriginal: playbook.required_to_hit_original?.monthly_avg,
                    trailingPace: playbook.pace?.revenue,
                  }))}
                  margin={{ left: 20, right: 20, top: 20, bottom: 10 }}
                >
                  <CartesianGrid {...CHART_GRID} />
                  <XAxis dataKey="label" {...CHART_AXIS} tick={{ fontSize: 11 }} />
                  <YAxis {...CHART_AXIS} tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={CHART_TOOLTIP}
                    formatter={(v) => v == null ? '—' : moneyFull(v)} />
                  <Legend iconType="rect" wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="adjustedPlan" name="Adjusted plan (BHAG monthly)"
                    stroke="#1D57A5" strokeWidth={3} dot={{ r: 4 }} connectNulls />
                  <Line type="monotone" dataKey="originalPlan" name="Original plan (row 2)"
                    stroke="#141B4D" strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls />
                  <Line type="monotone" dataKey="requiredOriginal" name="Required run-rate · hit original"
                    stroke="#B4530C" strokeWidth={2} strokeDasharray="3 3" dot={false} connectNulls />
                  <Line type="monotone" dataKey="trailingPace" name={`Trailing ${playbook.trailing_window}-mo pace`}
                    stroke="#0F766E" strokeWidth={2} strokeDasharray="2 4" dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Supporting metrics grid — sales count + calls booked per remaining month */}
          <div className="border border-indie-ink/10 bg-white p-4" data-testid="playbook-supporting-metrics">
            <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">
              Supporting monthly targets · sales · calls · qualification · close
            </div>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/50 font-semibold border-b border-indie-ink/10">
                    <th className="text-left py-2 pr-3">Month</th>
                    <th className="text-right py-2 px-2">Revenue (adj)</th>
                    <th className="text-right py-2 px-2">Sales</th>
                    <th className="text-right py-2 px-2">Calls booked</th>
                    <th className="text-right py-2 px-2">Calls had</th>
                    <th className="text-right py-2 px-2">Qual %</th>
                    <th className="text-right py-2 pl-2">Close %</th>
                  </tr>
                </thead>
                <tbody>
                  {(playbook.remaining_months || []).map((rm) => (
                    <tr key={rm.month} className="border-b border-indie-ink/5" data-testid={`playbook-row-${rm.month}`}>
                      <td className="py-2 pr-3 text-indie-ink font-medium">{rm.label}</td>
                      <td className="py-2 px-2 text-right num-tabular">{moneyFull(rm.revenue_target_adjusted)}</td>
                      <td className="py-2 px-2 text-right num-tabular">{rm.sales_target ?? '—'}</td>
                      <td className="py-2 px-2 text-right num-tabular">{rm.calls_booked_target ?? '—'}</td>
                      <td className="py-2 px-2 text-right num-tabular">{rm.calls_target ?? '—'}</td>
                      <td className="py-2 px-2 text-right num-tabular">{rm.qualified_pct_target != null ? pct(rm.qualified_pct_target, 0) : '—'}</td>
                      <td className="py-2 pl-2 text-right num-tabular">{rm.conversion_target != null ? pct(rm.conversion_target, 0) : '—'}</td>
                    </tr>
                  ))}
                  <tr className="bg-indie-cream/60">
                    <td className="py-2 pr-3 text-[10px] uppercase tracking-[0.12em] text-indie-ink/60 font-semibold">Trailing {playbook.trailing_window}-mo pace</td>
                    <td className="py-2 px-2 text-right num-tabular text-indie-ink/70">{moneyFull(playbook.pace?.revenue)}</td>
                    <td className="py-2 px-2 text-right num-tabular text-indie-ink/70">{playbook.pace?.sales != null ? Math.round(playbook.pace.sales) : '—'}</td>
                    <td className="py-2 px-2 text-right num-tabular text-indie-ink/70">{playbook.pace?.calls_booked != null ? Math.round(playbook.pace.calls_booked) : '—'}</td>
                    <td className="py-2 px-2 text-right num-tabular text-indie-ink/70">{playbook.pace?.calls_had != null ? Math.round(playbook.pace.calls_had) : '—'}</td>
                    <td className="py-2 px-2 text-right num-tabular text-indie-ink/70">{playbook.pace?.qualified_pct != null ? pct(playbook.pace.qualified_pct, 1) : '—'}</td>
                    <td className="py-2 pl-2 text-right num-tabular text-indie-ink/70">{playbook.pace?.conversion != null ? pct(playbook.pace.conversion, 1) : '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Barriers & strategies */}
          <div className="border border-indie-ink/10 bg-white p-4" data-testid="playbook-barriers">
            <div className="flex items-center gap-2">
              <AlertTriangle size={13} className="text-amber-600" strokeWidth={1.5} />
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">
                Barriers to closing the year · levers below plan (ranked by revenue impact)
              </div>
            </div>
            {playbook.barriers.length === 0 && (
              <div className="mt-3 text-sm text-indie-ink/60 italic">
                No funnel factors are below plan on the trailing {playbook.trailing_window}-month pace — great pace to hold.
              </div>
            )}
            <ul className="mt-3 space-y-4">
              {playbook.barriers.map((b, i) => {
                const isRate = b.unit === 'rate';
                const isUsd = b.unit === 'usd';
                const fmt = (v) => isRate ? pct(v, 1) : (isUsd ? money(v) : Math.round(v).toLocaleString());
                return (
                  <li key={b.factor} className="border-l-4 border-amber-500 bg-amber-50/50 pl-3 pr-2 py-2"
                      data-testid={`playbook-barrier-${b.factor.toLowerCase().replace(/[\s()]+/g, '-').replace(/-$/,'')}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <div className="text-sm font-semibold text-indie-ink">
                          #{i + 1} {b.factor}
                        </div>
                        <div className="text-[11px] text-indie-ink/70">
                          Trailing pace {fmt(b.actual)} · plan avg {fmt(b.target)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-indie-ink num-tabular">
                          + {money(b.revenue_delta_if_fixed)} / month
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/50">if lifted to plan</div>
                      </div>
                    </div>
                    <div className="mt-2 flex items-start gap-2">
                      <Lightbulb size={12} className="text-indie-cobalt mt-0.5 shrink-0" strokeWidth={1.5} />
                      <ul className="text-[11px] text-indie-ink/75 space-y-1 leading-relaxed">
                        {b.strategies.map((s, j) => (
                          <li key={j} className="pl-1 border-l border-indie-cobalt/30">{s}</li>
                        ))}
                      </ul>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 text-[10px] text-indie-ink/50 pt-2 border-t border-indie-ink/10">
              Baseline funnel: revenue = Calls Had × Qualification Rate (of Calls Had) × Close Rate × Avg Price. Each barrier assumes only its own factor returns to the remaining-plan average while the others hold at trailing pace — deltas are per-month, not additive across levers.
            </div>
          </div>

          <div className="text-[10px] text-indie-ink/50 pt-1 border-t border-indie-ink/10">
            Source: {playbook.source}
          </div>
        </div>
      )}


      {/* Attainment percent over time — Revenue uses ORIGINAL targets (BHAG row 2)
          per user's explicit rule: historical attainment must always be
          measured against the goal set at the start of the year, never the
          adjusted target. Other funnel series (Sales, Calls Booked) use the
          Sales Snapshot goal since BHAG doesn't carry an "original" flavor
          for those. */}
      {ts && (
        <div className="border border-indie-ink/15 bg-white p-5" data-testid="plan-attainment-over-time">
          <div className="flex items-center gap-2 flex-wrap">
            <Target size={14} className="text-indie-ink/70" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
              Attainment percent · actual ÷ ORIGINAL plan by month
            </div>
            <ClockLabel type="cash" />
          </div>
          <div className="mt-3 h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={(() => {
                  const short = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                  const bykey = Object.fromEntries((ts.metrics || []).map((m) => [m.key, m]));
                  const rows = [];
                  for (let i = 0; i < 12; i++) {
                    const mnum = i + 1;
                    const row = { label: short[i] };
                    // Revenue attainment uses BHAG ORIGINAL target (row 2)
                    // — sourced directly from bhagByMonth so we don't rely
                    // on the shared timeseries goal (which is adjusted).
                    const bm = bhagByMonth[mnum] || {};
                    const revActual = bm.revenue_actual;
                    const revOrigTarget = bm.revenue_target_original || bm.revenue_target;
                    row.Revenue = (revActual != null && revOrigTarget) ? revActual / revOrigTarget : null;
                    // Other series stay on the sheet-based goal.
                    for (const [key, series] of [['Sales','sales_count'],['Calls Booked','calls_booked']]) {
                      const m = bykey[series]; if (!m) continue;
                      const a = (m.actual || []).find((p) => p.month === mnum)?.value;
                      const g = (m.goal || []).find((p) => p.month === mnum)?.value;
                      row[key] = (a != null && g) ? a / g : null;
                    }
                    rows.push(row);
                  }
                  return rows;
                })()}
                margin={{ left: 10, right: 20, top: 20, bottom: 10 }}
              >
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="label" {...CHART_AXIS} tick={{ fontSize: 11 }} />
                <YAxis {...CHART_AXIS} tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${Math.round(v * 100)}%`}
                  domain={[0, (dataMax) => Math.max(1.2, dataMax + 0.05)]} />
                <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => v == null ? '—' : `${(v * 100).toFixed(1)}%`} />
                <Legend iconType="rect" />
                <ReferenceLine y={1.0} stroke="#141B4D" strokeDasharray="1 4" strokeOpacity={0.5}
                  label={{ value: '100% plan', position: 'insideTopRight', fontSize: 10, fill: '#141B4D' }} />
                <Line type="monotone" dataKey="Revenue" stroke="#141B4D" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                <Line type="monotone" dataKey="Sales" stroke="#1D57A5" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                <Line type="monotone" dataKey="Calls Booked" stroke="#5A8C3B" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-[10px] text-indie-ink/50">
            Revenue ratio = Actual ÷ BHAG ORIGINAL target (row 2) · closed months only. Adjusted target is used for forward planning above, not historical attainment. YoY overlays disabled — history begins Jan 2026.
          </div>
        </div>
      )}

      {/* Protection Plans — panel intentionally hidden (rule 8).
          HubSpot private-app is missing the pipelines read scope, so
          recurring-revenue trend cannot be truthfully published. The gap
          is logged on Sales · Integrity via the hidden_panels feed. */}

      <div className="text-[11px] text-indie-ink/50 pt-2 border-t border-indie-ink/10">
        Hero target row: {d.sources?.hero_target} · Attainment row: {d.sources?.attainment_target} · Actual row: {d.sources?.actual}.
      </div>
    </div>
  );
}
